import {Form as AntForm} from 'antd';
import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';

import Form from '../../../../components/Form/Form';
import MobileModalForm from '../../../../components/Form/MobileModalForm';
import dataCollectorWorkModes from '../../../../consts/dataCollector/dataCollectorWorkModes.ts';
import DeviceGroupingTypes from '../../../../consts/devices/deviceGroupingTypes.ts';
import * as formInputNames from '../../../../consts/form/formInputNames';
import * as formItemTypes from '../../../../consts/form/formItemTypes';
import * as localizationKeys from '../../../../consts/localization/localizationKeys';
import useIsMobileViewport from '../../../../hooks/useIsMobileViewport';
import dataCollectorService from '../../../../services/dataCollectors/dataCollectorService';
import deviceService from '../../../../services/device/deviceService';
import {getLocalizedStrings} from '../../../../services/localization/localizationService';
import log from '../../../../services/logger/log';
import {setBuildingDataCollectors} from '../../../../state/ducks/buildings';
import {cleanDeviceModels, setDeviceInfo, setDevices} from '../../../../state/ducks/device';
import {makeSelectBuildingProfile} from '../../../../state/selectors/buildings';
import {makeSelectRoomProfile} from '../../../../state/selectors/rooms';
import {dispatch} from '../../../../state/store';
import {extractModelItems, filterModelsByWorkMode} from '../../../../utils/deviceModels';
import helpers from '../../../../utils/helpers';
import indicatorModalService from './deviceModalService';

const {useForm} = AntForm;

const DeviceModal = (props) => {
    const [form] = useForm();
    const isMobile = useIsMobileViewport();
    const localizedStrings = getLocalizedStrings();
    const {modalType, onCancel, onOk, buildingId: propBuildingID, roomId: propRoomID, isEditing} = props;

    const [currentWorkMode, setCurrentWorkMode] = useState(null);

    const {buildingID: stateBuildingID} = useSelector(makeSelectBuildingProfile()) || {};
    const {roomID: stateRoomID} = useSelector(makeSelectRoomProfile()) || {};

    const buildingID = propBuildingID || stateBuildingID;
    const roomID = propRoomID || stateRoomID;

    const {formFields, title, submitButton, cancelButton, initialValues, requireDeps} =
        indicatorModalService.getFormContent(modalType, buildingID, roomID, isEditing, currentWorkMode) || {};

    useEffect(() => {
        if (initialValues) {
            form.setFieldsValue(initialValues);
            const initialUspdId = initialValues[formInputNames.PZPD_ID]?.id;

            if (isEditing && initialUspdId) {
                dataCollectorService.getDataCollectorById(initialUspdId, (response) => {
                    const uspdDetails = response?.data || response;
                    const workMode = uspdDetails?.workMode ?? uspdDetails?.WorkMode;

                    setCurrentWorkMode(workMode);
                });
            }
        }

        return () => {
            dispatch(cleanDeviceModels());
            dispatch(setDeviceInfo(null));
            dispatch(setDevices(null));
            dispatch(setBuildingDataCollectors(null));
        };
    }, []);

    const onFinish = async () => {
        try {
            const values = await form.validateFields();

            let formValues = values;

            if (initialValues) {
                formValues = {...initialValues, ...values};
            }

            helpers.runFunction(onOk, formValues);
        } catch (error) {
            log.error('Validate Failed:', error);
        }
    };

    const resetFormFields = (fieldNames) => {
        const fieldsToReset = {};

        fieldNames.forEach((name) => (fieldsToReset[name] = null));
        form.setFieldsValue(fieldsToReset);
    };

    const setFormField = (name, key, value) => form.setFieldsValue({[name]: {id: key, value}});

    const revalidateIfTouched = (fieldNames) => {
        const touchedFields = fieldNames.filter((fieldName) => form.isFieldTouched(fieldName));

        if (touchedFields.length) {
            form.validateFields(touchedFields).catch(() => {});
        }
    };

    const handleSelect = async (_, name, option) => {
        const currentValue = form.getFieldValue(name);

        if (currentValue?.value === option.value) return;
        const groupID = option.groupID || null;

        setFormField(name, option.key, option.value, groupID);

        switch (name) {
            case formInputNames.DEVICE_TYPE_ID: {
                const selectedDeviceGroupID = option.key;
                const response = await deviceService.getDevicesModelsByGroupID(selectedDeviceGroupID);
                const filtered = filterModelsByWorkMode(extractModelItems(response), currentWorkMode);

                resetFormFields([formInputNames.FACTORY_ID, formInputNames.WATER_DEVICE_ID]);

                form.setFields([
                    {
                        name: formInputNames.DEVICE_MODEL_ID,
                        value: undefined,
                        props: {options: filtered},
                    },
                ]);
                break;
            }

            case formInputNames.PZPD_ID:
            case formInputNames.GROUPING_TYPE: {
                const buildingId = form.getFieldValue(formInputNames.BUILDING_ID);
                const isDataCollectorExist = form.getFieldValue(formInputNames.PZPD_ID);
                const groupingTypeId = form.getFieldValue(formInputNames.GROUPING_TYPE)?.id;
                const dataCollectorId = form.getFieldValue(formInputNames.PZPD_ID)?.id;

                if (name === formInputNames.PZPD_ID) {
                    if (dataCollectorId) {
                        dataCollectorService.getDataCollectorById(dataCollectorId, async (response) => {
                            const uspdDetails = response?.data || response;
                            const workMode = uspdDetails?.workMode ?? uspdDetails?.WorkMode;

                            setCurrentWorkMode(workMode);

                            resetFormFields([formInputNames.DEVICE_MODEL_ID]);

                            if (workMode !== dataCollectorWorkModes.Gateway) {
                                resetFormFields([
                                    formInputNames.DEV_EUI,
                                    formInputNames.JOIN_EUI,
                                    formInputNames.APP_KEY,
                                ]);
                            }

                            const currentTypeId =
                                form.getFieldValue(formInputNames.DEVICE_TYPE_ID)?.id ||
                                form.getFieldValue(formInputNames.DEVICE_TYPE_ID);

                            if (currentTypeId) {
                                const modelsResp = await deviceService.getDevicesModelsByGroupID(currentTypeId);
                                const filtered = filterModelsByWorkMode(extractModelItems(modelsResp), workMode);

                                form.setFields([
                                    {
                                        name: formInputNames.DEVICE_MODEL_ID,
                                        props: {options: filtered},
                                    },
                                ]);
                            }

                            setTimeout(() => {
                                revalidateIfTouched([
                                    formInputNames.MANUAL_ID,
                                    formInputNames.DEV_EUI,
                                    formInputNames.DEVICE_MODEL_ID,
                                ]);
                            }, 100);
                        });
                    } else {
                        setCurrentWorkMode(null);
                        resetFormFields([
                            formInputNames.DEVICE_MODEL_ID,
                            formInputNames.DEV_EUI,
                            formInputNames.JOIN_EUI,
                            formInputNames.APP_KEY,
                        ]);

                        setTimeout(() => revalidateIfTouched([formInputNames.MANUAL_ID]), 100);
                    }
                }

                const isDistributedType = groupingTypeId === DeviceGroupingTypes.Distributed;
                const isGroupType = groupingTypeId === DeviceGroupingTypes.Grouped;

                if ((isDistributedType || isGroupType) && !roomID) {
                    resetFormFields([formInputNames.PARENT_DEVICE_ID, formInputNames.ROOM_ID]);
                } else if (isDistributedType || isGroupType) {
                    resetFormFields([formInputNames.PARENT_DEVICE_ID]);
                }

                if ((isDistributedType || isGroupType) && isDataCollectorExist) {
                    await deviceService.getDevices({
                        buildingId,
                        groupingType: groupingTypeId,
                        dataCollectorId,
                    });
                }
                break;
            }
        }
    };

    const handleChange = () => {};

    if (isMobile) {
        return (
            <MobileModalForm
                form={form}
                formName={`device-mobile-${modalType}`}
                title={title}
                closeLabel={localizedStrings[localizationKeys.DC_PAGE_MOBILE_CLOSE_FORM]}
                formFields={formFields.map((field) => ({
                    ...field,
                    inputMode: field.name === formInputNames.MANUAL_ID ? 'numeric' : undefined,
                    ...(field.formItemType === formItemTypes.DATE_PICKER && {
                        inputReadOnly: true,
                        popupClassName: 'mobile-modal-calendar',
                    }),
                }))}
                initialValues={initialValues}
                requireDeps={requireDeps}
                onFinish={onFinish}
                onCancel={onCancel}
                submitButton={submitButton}
                cancelButton={cancelButton}
                handleSelect={handleSelect}
                handleChange={handleChange}
            />
        );
    }

    return (
        <Form
            formClassName='device-form'
            containerClassName='device-form-content'
            buttonsClassName='device-form-actions'
            requireDeps={requireDeps}
            handleSelect={handleSelect}
            closeModal={onCancel}
            initialValues={initialValues}
            submitButton={submitButton}
            onFinish={onFinish}
            title={title}
            formFields={formFields}
            cancelButton={cancelButton}
            formInstance={form}
            handleChange={handleChange}
        />
    );
};

export default React.memo(DeviceModal);
